import React, { useMemo } from "react";
import Highcharts from "highcharts/highstock";
import AnnotationsModule from "highcharts/modules/annotations";
import HighchartsReact from "highcharts-react-official";
import { darken, useMediaQuery, useTheme } from "@mui/material";
import { toCustomShort, toPercent, useDateFormatterForXAxis } from "@helpers";
import {
  SecuritiesChartSelectedType,
  SecuritiesChartType,
} from "@components/Chart/SecuritiesChart";
import { useChartColors } from "@hooks";
import { ComparisonValue } from "@components/Chart/BaseChartTools";
import { renderToStaticMarkup, renderToString } from "react-dom/server";
import { updateTooltipPosition } from "../helper";
import { ChartSeriesData } from "../common";
import ChartHandlerIcon from "./chart-handler-light.svg?react";
import { CustomTooltip } from "./CustomTooltip";
import { Marker } from "./types";
import { getChartRangeValues } from "./utils";
import { useDrawing } from "./useDrawing";
import { useGetMandatorStyles } from "./mandatorStyles";

AnnotationsModule(Highcharts);

const getChartHandlerUrl = ({
  color,
  innerColor,
}: {
  color: string;
  innerColor: string;
}) => {
  const chartHandlerSvg = renderToStaticMarkup(
    <ChartHandlerIcon
      color={color}
      style={
        {
          "--navigator-handle-inner": innerColor,
        } as React.CSSProperties
      }
    />
  );

  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
    chartHandlerSvg
  )}`;
};

export const useChartOptions = (
  chartData: ChartSeriesData[],
  allowDrawing: boolean,
  chartType: SecuritiesChartType,
  showTooltipMobile: boolean,
  chartComponentRef: React.RefObject<HighchartsReact.RefObject | null>,
  markers?: Marker[],
  mainValueLabel?: string,
  currency?: string,
  comparedTicker?: ComparisonValue,
  onChartClick?: (x?: number) => void,
  tooltipSubtitleMap?: Record<string, string | undefined>,
  showNavigator?: boolean
): Highcharts.Options => {
  const theme = useTheme();
  const chartColors = useChartColors("securitiesChart");
  const isMD = useMediaQuery(theme.breakpoints.down("md"));
  const isXS = useMediaQuery(theme.breakpoints.down("xs"));
  const { annotations, isDrawing } = useDrawing(
    allowDrawing,
    chartData,
    chartColors.drawableLines,
    chartComponentRef?.current?.chart
  );
  const isValuationChart = chartType === "valuation";
  const mandatorStyles = useGetMandatorStyles();
  const chartRange = getChartRangeValues(chartData, isValuationChart);
  const shouldShowNavigator = showNavigator && chartData.length > 0;
  const xAxisFormatter = useDateFormatterForXAxis();

  const chartHandlerSvg = getChartHandlerUrl({
    color: theme.palette.button.primaryButton.default,
    innerColor: theme.palette.icon.onColorDark,
  });

  const xAxisTimeRangeMs = useMemo(() => {
    let minX = Infinity;
    let maxX = -Infinity;
    for (const series of chartData) {
      for (const [x] of series.data) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
      }
    }
    return minX !== Infinity && maxX !== -Infinity ? maxX - minX : 0;
  }, [chartData]);

  const isIntradayRange =
    xAxisTimeRangeMs > 0 && xAxisTimeRangeMs < 24 * 60 * 60 * 1000;
  const xAxisMinTickInterval = isIntradayRange
    ? 60 * 60 * 1000
    : 24 * 60 * 60 * 1000;

  return useMemo(() => {
    const lineOrAreaColor = (data: ChartSeriesData) =>
      data.areaColor ?? data.color ?? theme.palette.common.white;

    const flagsSeries = {
      id: "flags",
      type: "flags",
      accessibility: {
        exposeAsGroupOnly: true,
        description: "Flagged events.",
      },
      data: markers,
      onSeries: "all",
      shape: "circlepin",
      enableMouseTracking: false,
      width: 16,
      height: 16,
      clip: false,
      states: {
        inactive: {
          opacity: 1,
        },
      },
    };
    const mainSeries = chartData.map((data) => {
      const color = lineOrAreaColor(data);
      return {
        ...data,
        id: data.name,
        marker: { enabled: false },
        threshold: null,
        fillColor: {
          linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
          stops: [
            [0, Highcharts.color(color).setOpacity(0.1).get("rgba")],
            [1, Highcharts.color(color).setOpacity(0).get("rgba")],
          ],
        },
      };
    });

    return {
      credits: { enabled: false },
      chart: {
        type: "spline",
        height: shouldShowNavigator ? 440 : 240,
        animation: false,
        zooming: { mouseWheel: false },
        backgroundColor: "transparent",
        plotBackgroundColor: "transparent",
        spacingBottom: shouldShowNavigator ? 30 : 0,
      },
      series: [...mainSeries, flagsSeries],
      title: {
        text: "",
      },
      tooltip: {
        split: true,
        borderColor: "transparent",
        backgroundColor: "transparent",
        shadow: false,
        useHTML: true,
        positioner: function (_, labelHeight, point) {
          return updateTooltipPosition(
            labelHeight,
            point,
            this.chart,
            isXS,
            ".custom-tooltip-container"
          );
        },
        formatter: function () {
          if ((!showTooltipMobile && isMD) || isDrawing.current) return "";

          const marker = markers?.find((marker) => marker.data[0] === this.x);

          const points =
            this.points?.map((x) => ({
              value: x.y as number,
              color: x.color as string,
              name: x.series.name as SecuritiesChartSelectedType | "comparison",
              displayName: tooltipSubtitleMap?.[x.series.name],
            })) ?? [];

          return renderToString(
            <CustomTooltip
              points={points}
              events={marker?.tooltipData ?? []}
              dateTime={(this.x as number) ?? 0}
              comparedTicker={comparedTicker}
              mainValueLabel={mainValueLabel}
              chartType={chartType}
              currency={currency}
              theme={theme}
            />
          );
        },
      },
      annotations,
      xAxis: {
        type: "datetime",
        lineColor: theme.palette.border.secondary,
        tickLength: 0,
        minPadding: 0,
        maxPadding: 0,
        minTickInterval: xAxisMinTickInterval,
        crosshair: {
          color: theme.palette.border.secondary,
        },
        labels: {
          style: {
            fontSize: theme.typography.body3.fontSize,
            lineHeight: theme.typography.body3.lineHeight,
            fontWeight: mandatorStyles.fontWeight.xAxis,
            color: theme.palette.text.secondary,
            fontFamily: theme.typography.fontFamily,
          },
          formatter: function (
            data: Highcharts.AxisLabelsFormatterContextObject
          ) {
            const selectedDates = this.chart.xAxis[0].getExtremes();
            const startDate = new Date(selectedDates.min);
            const endDate = new Date(selectedDates.max);
            return xAxisFormatter(
              startDate,
              endDate,
              data.value,
              (this.axis as unknown as { tickInterval: number }).tickInterval
            );
          },
        },
      },
      yAxis: {
        min: chartRange.min,
        max: chartRange.max,
        gridLineColor: theme.palette.border.secondary,
        title: {
          text: "",
        },
        labels: {
          style: {
            fontSize: theme.typography.body3.fontSize,
            lineHeight: theme.typography.body3.lineHeight,
            fontWeight: mandatorStyles.fontWeight.yAxis,
            color: theme.palette.text.secondary,
            fontFamily: theme.typography.fontFamily,
          },
          formatter: function (data) {
            return renderToString(
              <>
                {chartType === "performance"
                  ? toPercent((data.value as number) / 100, "auto", 0, 0)
                  : toCustomShort(data.value as number)}
              </>
            );
          },
        },
      },
      plotOptions: {
        spline: {
          lineWidth: 2,
          marker: {
            enabled: false,
          },
        },
        area: {
          lineWidth: 2,
          marker: {
            enabled: false,
          },
        },
        series: {
          cursor: "pointer",
          point: {
            events: {
              click: function () {
                if (!onChartClick) return;
                const x = this.x;
                if (typeof x !== "number") {
                  return;
                }

                onChartClick(x);
              },
              mouseOver: function () {
                if (!onChartClick) return;

                const x = this.x;
                const chart = this.series?.chart as
                  | Highcharts.Chart
                  | undefined;

                if (!chart || typeof x !== "number" || !chart.mouseIsDown) {
                  return;
                }

                onChartClick(x);
              },
            },
          },
        },
      },
      legend: {
        enabled: false,
      },
      ...(shouldShowNavigator && {
        navigator: {
          enabled: true,
          height: 75,
          series: {
            type: "spline",
            color: theme.palette.primary.main,
            data:
              chartData.find((s) => s.name === "all")?.data ??
              chartData[0]?.data ??
              [],
          },
          xAxis: {
            lineWidth: 1,
            type: "datetime",
            tickLength: 0,
            lineColor: darken(theme.palette.border.primary, 0.05),
            crosshair: {
              color: darken(theme.palette.border.primary, 0.05),
            },
            gridLineColor: darken(theme.palette.border.primary, 0.05),
            minorGridLineWidth: 10,
            labels: {
              overflow: "allow",
              align: "center",
              x: 0,
              y: 24,
              reserveSpace: false,
              style: {
                color: theme.palette.text.secondary,
                textOutline: "none",
                fontSize: theme.typography.body3.fontSize,
                lineHeight: theme.typography.body3.lineHeight,
                fontFamily: theme.typography.fontFamily,
                fontWeight: mandatorStyles.fontWeight.navigator,
                opacity: 1,
              },
              // @ts-ignore
              formatter: function (data) {
                try {
                  // @ts-ignore
                  const axis = this.axis;
                  // @ts-ignore
                  const chart = axis?.chart?.chart || this.chart;
                  // @ts-ignore
                  if (
                    chart &&
                    chart.xAxis &&
                    Array.isArray(chart.xAxis) &&
                    chart.xAxis.length > 0 &&
                    chart.xAxis[0]
                  ) {
                    // @ts-ignore
                    const extremes = chart.xAxis[0].getExtremes();
                    if (
                      extremes &&
                      extremes.min != null &&
                      extremes.max != null
                    ) {
                      const startDate = new Date(extremes.min);
                      const endDate = new Date(extremes.max);
                      return renderToString(
                        <div>
                          {xAxisFormatter(
                            startDate,
                            endDate,
                            data.value,
                            // @ts-ignore
                            axis?.tickInterval
                          )}
                        </div>
                      );
                    }
                  }
                } catch (error) {
                  // Silently fail and use fallback
                }
                // Fallback: use a simple date format
                return renderToString(
                  <div>
                    {xAxisFormatter(new Date(), new Date(), data.value)}
                  </div>
                );
              },
            },
          },
          outlineWidth: 0,
          maskFill: {
            linearGradient: {
              x1: 0,
              y1: 0,
              x2: 0,
              y2: 1,
            },
            stops: [
              [
                0,
                Highcharts.color(theme.palette.primary.main)
                  .setOpacity(0)
                  .get("rgba") as string,
              ],
              [
                1,
                Highcharts.color(theme.palette.primary.main)
                  .setOpacity(0.1)
                  .get("rgba") as string,
              ],
            ],
          },
          handles: chartHandlerSvg
            ? {
                enabled: true,
                height: 75,
                width: 26,
                symbols: [`url(${chartHandlerSvg})`, `url(${chartHandlerSvg})`],
              }
            : {
                enabled: true,
              },
        },
      }),
    } as Highcharts.Options;
  }, [
    annotations,
    chartData,
    chartHandlerSvg,
    chartRange,
    chartType,
    comparedTicker,
    currency,
    xAxisFormatter,
    isDrawing,
    isMD,
    isXS,
    mainValueLabel,
    mandatorStyles,
    markers,
    onChartClick,
    shouldShowNavigator,
    showTooltipMobile,
    theme,
    tooltipSubtitleMap,
    xAxisMinTickInterval,
  ]);
};
